/* ============================================================
   ProviderPortal — main dashboard: Profile / Printers / Orders
   ============================================================ */

import { useEffect, useState } from 'react';
import { api } from '../api/client';
import type { ProviderTab, ProviderProfileDetail } from '../types/provider';
import { ProviderProfilePanel } from './ProviderProfilePanel';
import { ProviderPrintersPanel } from './ProviderPrintersPanel';
import { ProviderOrdersPanel } from './ProviderOrdersPanel';
import { ProviderProductsPanel } from './ProviderProductsPanel';
import { ProviderPricingPanel } from './ProviderPricingPanel';
import { ProviderPayoutPanel } from './ProviderPayoutPanel';
import { ProviderDeliverySettingsCard } from './ProviderDeliverySettingsCard';
import { ProviderMaterialStockPanel } from './ProviderMaterialStockPanel';
import { ProviderEarningsPanel } from './ProviderEarningsPanel';
import { ProviderReferralPanel } from './ProviderReferralPanel';
import { NalogConnectionCard } from './NalogConnectionCard';
import { BambuConnectionCard } from './BambuConnectionCard';

export function ProviderPortal({
  initialTab,
  initialProductEdit,
  onProductEditNavigate,
}: {
  initialTab?: ProviderTab;
  /** 0e895f9a: 'new' | listing Guid from /provider/products/* — mounts the
   *  products tab straight on the separate edit page (deep link). */
  initialProductEdit?: string | null;
  /** 0e895f9a: open ('new'/guid) or close (null) the edit page — App keeps
   *  the URL in sync (/provider/products/new|:id ↔ /provider). */
  onProductEditNavigate?: (target: string | null) => void;
} = {}) {
  // F3 (268dfd51): /provider/orders + provider-notification deep links
  // mount the portal on a chosen tab; default stays Профиль. Unregistered
  // providers fall through to the register panel exactly as before (tab
  // state is irrelevant there). 0e895f9a: a product-edit deep link wins over
  // the tab default — it always lands on Товары.
  const [tab, setTab] = useState<ProviderTab>(
    initialProductEdit ? 'products' : initialTab ?? 'profile',
  );
  const [profile, setProfile] = useState<ProviderProfileDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notRegistered, setNotRegistered] = useState(false);

  const loadProfile = async () => {
    setLoading(true);
    setError(null);
    try {
      const p = await api.getProviderProfile();
      setProfile(p);
      setNotRegistered(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Ошибка загрузки';
      if (msg.includes('not found') || msg.includes('Register')) {
        setNotRegistered(true);
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadProfile(); }, []);

  if (loading) {
    return (
      <div className="container" style={{ paddingTop: 'var(--space-2xl)', textAlign: 'center' }}>
        <div className="spinner" style={{ margin: '0 auto' }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container" style={{ paddingTop: 'var(--space-2xl)' }}>
        <div className="card" style={{ textAlign: 'center' }}>
          <p style={{ fontSize: 'var(--fs-body)', color: 'var(--color-danger)' }}>{error}</p>
          <button className="btn btn-secondary" onClick={loadProfile} style={{ marginTop: 'var(--space-md)' }}>
            Повторить
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ paddingTop: 'var(--space-xl)', paddingBottom: 'var(--space-2xl)' }}>
      {/* Heading */}
      <div style={{ marginBottom: 'var(--space-xl)' }}>
        <h1 style={{ fontSize: 'var(--fs-h1)', fontWeight: 'var(--fw-bold)', letterSpacing: '-0.02em' }}>
          Кабинет исполнителя
        </h1>
        {profile && (
          <p style={{ fontSize: 'var(--fs-body)', color: 'var(--color-text-secondary)', marginTop: 'var(--space-xs)' }}>
            {profile.companyName}
            {profile.isVerified && (
              <span className="badge badge-success" style={{ marginLeft: 'var(--space-sm)' }}>✓ Проверен</span>
            )}
          </p>
        )}
      </div>

      {/* Tab bar */}
      <div
        style={{
          display: 'flex',
          gap: 'var(--space-xs)',
          marginBottom: 'var(--space-lg)',
          background: 'var(--color-separator)',
          borderRadius: 'var(--radius-lg)',
          padding: '3px',
        }}
      >
        <TabButton active={tab === 'profile'} onClick={() => setTab('profile')} label="Профиль" />
        <TabButton active={tab === 'printers'} onClick={() => setTab('printers')} label="Принтеры" />
        <TabButton active={tab === 'orders'} onClick={() => setTab('orders')} label="Заказы" disabled={!profile} />
        <TabButton active={tab === 'earnings'} onClick={() => setTab('earnings')} label="Доходы" disabled={!profile} />
        <TabButton active={tab === 'products'} onClick={() => setTab('products')} label="Товары" />
        <TabButton active={tab === 'pricing'} onClick={() => setTab('pricing')} label="Тарифы" />
        <TabButton active={tab === 'stock'} onClick={() => setTab('stock')} label="Склад" />
      </div>

      {/* Tab content */}
      {notRegistered ? (
        <ProviderProfilePanel mode="register" onRegistered={loadProfile} />
      ) : tab === 'profile' ? (
        // SD-4: the payout-destination card rides the profile tab — a
        // sibling, not a nested form (profile submit must not swallow it).
        // 2f9c70ba: the referral card rides the same pattern.
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)' }}>
          <ProviderProfilePanel mode="edit" profile={profile} onUpdated={loadProfile} />
          <ProviderReferralPanel />
          <ProviderPayoutPanel />
          {/* 138b4149: delivery settings (sender postal index for Почта
              России live tariffs) — same sibling-card pattern. */}
          <ProviderDeliverySettingsCard />
          {/* 15c2a311: «Мой налог» SMS connection rides the same sibling-card
              pattern as the payout destination (SD-4). */}
          <NalogConnectionCard />
          {/* dfb793af (1cf7d337 part 3): Bambu Lab account + printers —
              same sibling-card pattern; token-free by backend contract. */}
          <BambuConnectionCard />
        </div>
      ) : tab === 'printers' ? (
        <ProviderPrintersPanel profile={profile} />
      ) : tab === 'earnings' ? (
        // CB-4 (TASK-cf5f4532): read-only earnings over the SD-1/SD-2
        // snapshot fields — где деньги, а не кнопки выплат.
        <ProviderEarningsPanel />
      ) : tab === 'products' ? (
        <ProviderProductsPanel
          editId={initialProductEdit ?? null}
          onOpenEdit={(id) => onProductEditNavigate?.(id)}
          onCloseEdit={() => onProductEditNavigate?.(null)}
        />
      ) : tab === 'pricing' ? (
        <ProviderPricingPanel />
      ) : tab === 'stock' ? (
        // MS-1 (TASK-52194f26): opt-in material stock by color — rows feed
        // the customer color picker at performer selection.
        <ProviderMaterialStockPanel />
      ) : (
        <ProviderOrdersPanel />
      )}
    </div>
  );
}

function TabButton({
  active, onClick, label, disabled,
}: { active: boolean; onClick: () => void; label: string; disabled?: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        flex: 1,
        padding: '10px var(--space-md)',
        borderRadius: 'var(--radius-md)',
        fontSize: 'var(--fs-body)',
        fontWeight: active ? 'var(--fw-semibold)' : 'var(--fw-medium)',
        color: active ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
        background: active ? 'var(--color-surface)' : 'transparent',
        transition: 'all var(--transition-fast)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
      }}
    >
      {label}
    </button>
  );
}
